import { LinearProgress } from '@mui/joy';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import {
  useChangePaymentPlanMutation,
  useOnPaymentMethodSuccess,
  useOrganizationId,
} from '@/hooks/useSwr';
import {
  ChangePaymentPlanTierSchema,
  StripeBusinessInfo,
  StripePaymentInformation,
} from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';

/*
  This component is only hit if stripe required a redirect.
  Otherwise the billing success flow is hadled in the AddPaymentInfoModalCard component.
*/
export default function PaymentSetupPage() {
  const [searchParams] = useSearchParams();
  const targetTier = searchParams.get(
    'target_tier'
  ) as ChangePaymentPlanTierSchema;
  const redirectStatus = searchParams.get('redirect_status');
  const billingInfoRaw = searchParams.get('billinginfo');
  const setupIntent = searchParams.get('setup_intent');

  const taxId = searchParams.get('tax_id');
  const taxIdType = searchParams.get('tax_id_type');
  const businessName = searchParams.get('business_name');
  const invoiceEmail = searchParams.get('invoice_email');
  const isBusiness = searchParams.get('is_business') === 'true';

  const billingInfo = billingInfoRaw ? JSON.parse(billingInfoRaw) : null;
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const paymentEnabledFF = useInstanceFlagOrDefault(
    InstanceFlags.payment_enabled
  ) as boolean;
  const [hasSentMutation, setHasSentMutation] = useState(false);
  const { trigger, isMutating } = useOnPaymentMethodSuccess();
  const { trigger: changePlanTrigger, isMutating: changingPlan } =
    useChangePaymentPlanMutation({});

  const { isLegacy } = useCurrentTier();

  const navigateToBillingStep = (step) => {
    navigate(
      `/${appOrganizationPath}/${organizationId}/settings/payments${
        step ? `?billing_step=${step}` : ''
      }`
    );
  };
  const { value: enablePricingRedesign } = useOrgConfig(
    OrgConfigs.enable_pricing_redesign
  );

  useEffect(() => {
    if (
      billingInfo &&
      paymentEnabledFF &&
      !isMutating &&
      !changingPlan &&
      !hasSentMutation &&
      redirectStatus === 'succeeded' &&
      setupIntent
    ) {
      setHasSentMutation(true);

      const paymentInfo: StripePaymentInformation = {
        billing_info: billingInfo,
        setup_intent: setupIntent,
      };
      const updatedInfo: StripeBusinessInfo = {
        company_info: {
          name: businessName ?? '',
        },
        invoice_email: invoiceEmail ?? '',
        is_business: isBusiness,
      };

      if (taxId && taxIdType) {
        updatedInfo.tax_id = {
          value: taxId,
          type: taxIdType,
        };
      }

      trigger({
        paymentInfo,
        updatedInfo,
        isBusiness: !!isBusiness,
      })
        .then(() => {
          if (targetTier) {
            return changePlanTrigger({ json: { tier: targetTier } }).then(
              () => {
                navigateToBillingStep(
                  isLegacy ? 'claim_gift' : 'subscribe_success'
                );
              }
            );
          }
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            console.error(e);
            navigateToBillingStep(
              `subscribe_error&error_message=${encodeURIComponent(e.message)}`
            );
          }
        });
    } else if (
      (paymentEnabledFF === false || redirectStatus !== 'succeeded') &&
      organizationId
    ) {
      navigate(
        `/${appOrganizationPath}/${organizationId}/settings/payments${
          !enablePricingRedesign ? '?tab=2' : ''
        }`
      );
    }
  }, [paymentEnabledFF]);

  return (
    <div className="p-10">
      <LinearProgress />
    </div>
  );
}
